import { Alert, styled } from '@mui/material';
import { isNotEmpty } from 'ramda';
import { useTranslation } from 'react-i18next';

import { ChatThinkingMessage } from '@/legacy/components/Chat/ChatMessage/ChatThinkingMessage';
import { I18nNamespace } from '~/common/types';
import { ChatConversation } from '~/components/Chat/ChatConversation';
import { MessageSubmissionContextProvider } from '~/components/Chat/MessageSubmissionContextProvider/MessageSubmissionContextProvider';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { Disclaimer } from '~/components/ConversationInput/components/Disclaimer/Disclaimer';
import { ConversationInput } from '~/components/ConversationInput/ConversationInput';
import { ConversationLayout } from '~/components/ConversationLayout/ConversationLayout';
import { conversationLayoutClasses } from '~/components/ConversationLayout/ConversationLayout.styled';
import { PageLayout } from '~/components/PageLayout/PageLayout';

import { IntakeChatHeader } from './components/IntakeChatHeader';
import { useIntakeChat } from './hooks/useIntakeChat';
import { FILE_UPLOAD_ENABLED_AGENT_IDS } from './IntakeChat.utils';

const StyledPageLayout = styled(PageLayout)(() => ({
  maxHeight: '100vh',
  '& > div:last-of-type': {
    flexGrow: 1,
    minHeight: 0,
  },
}));

export const IntakeChat = () => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'Chat' });

  const { isLoading, status, messages, error, agentId, handleSubmit, sendMessage } = useIntakeChat();

  const isInputDisabled = isLoading || (status !== 'ready' && status !== 'error');
  const isInputLoading = status === 'submitted' || status === 'streaming';

  // use chat on endpoint with agent
  return (
    <StyledPageLayout disablePadding fullHeight renderHeader={<IntakeChatHeader />}>
      <ConversationLayout
        sx={{
          [`& .${conversationLayoutClasses.conversationContainer}, & .${conversationLayoutClasses.inputContainer} > *`]:
            {
              maxWidth: {
                xl: 1280,
              },
            },
          [`& .${conversationLayoutClasses.inputContainer}`]: {
            background: `linear-gradient(0deg, #FAFAFA, 95%, transparent)`,
          },
        }}
        input={
          <ConversationInput
            placeholder={t('input.placeholder')}
            isLoading={isInputLoading}
            disabled={isInputDisabled}
            enableAttachments={agentId ? FILE_UPLOAD_ENABLED_AGENT_IDS[agentId] : false}
            onSubmit={handleSubmit}
          />
        }
        disclaimer={<Disclaimer />}
      >
        {isLoading && <CircularProgressCentered />}

        {isNotEmpty(messages) && sendMessage && (
          <MessageSubmissionContextProvider sendMessage={sendMessage}>
            <ChatConversation messages={messages} status={status}>
              {error && <Alert severity="error">{error.message}</Alert>}
              {status === 'submitted' && <ChatThinkingMessage />}
            </ChatConversation>
          </MessageSubmissionContextProvider>
        )}
      </ConversationLayout>
    </StyledPageLayout>
  );
};
